import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";
import { Json } from "@/integrations/supabase/types";

export interface Call {
  id: string;
  tenant_id: string;
  lead_id: string | null;
  contact_id: string | null;
  deal_id: string | null;
  caller_user_id: string | null;
  direction: "inbound" | "outbound";
  status: "scheduled" | "in_progress" | "completed" | "missed" | "cancelled";
  phone_number: string | null;
  duration_seconds: number;
  recording_url: string | null;
  transcript: string | null;
  ai_summary: string | null;
  ai_sentiment: "positive" | "neutral" | "negative" | "mixed" | null;
  ai_next_steps: string | null;
  ai_key_topics: string[] | null;
  notes: string | null;
  scheduled_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  metadata: Json;
  created_at: string;
  updated_at: string;
  contacts?: { first_name: string; last_name: string; phone: string } | null;
  leads?: { first_name: string; last_name: string; company_name: string } | null;
  deals?: { title: string } | null;
}

export function useCalls() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["calls", profile?.tenant_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("calls")
        .select("*, contacts(first_name, last_name, phone), leads(first_name, last_name, company_name), deals(title)")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Call[];
    },
    enabled: !!profile?.tenant_id,
  });
}

export function useCreateCall() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (call: {
      lead_id?: string | null;
      contact_id?: string | null;
      deal_id?: string | null;
      direction: string;
      status?: string;
      phone_number?: string;
      duration_seconds?: number;
      notes?: string;
      transcript?: string;
      started_at?: string;
      ended_at?: string;
      scheduled_at?: string;
    }) => {
      const { data, error } = await supabase
        .from("calls")
        .insert([{ ...call, tenant_id: profile?.tenant_id }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calls"] });
      toast.success("Call logged successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to log call: ${error.message}`);
    },
  });
}

export function useUpdateCall() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: {
      id: string;
      notes?: string;
      transcript?: string;
      ai_summary?: string;
      ai_sentiment?: string;
      ai_next_steps?: string;
      ai_key_topics?: string[];
      status?: string;
    }) => {
      const { data, error } = await supabase
        .from("calls")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calls"] });
      toast.success("Call updated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update call: ${error.message}`);
    },
  });
}

export function useDeleteCall() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("calls").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calls"] });
      toast.success("Call deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete call: ${error.message}`);
    },
  });
}

export function useAnalyzeCall() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ callId, call }: {
      callId: string;
      call: {
        transcript: string;
        duration_seconds: number;
        direction: string;
        contact_name?: string;
        deal_title?: string;
      };
    }) => {
      const { data, error } = await supabase.functions.invoke("analyze-call", {
        body: { call },
      });

      if (error) throw error;
      
      // Update the call with AI analysis
      const { error: updateError } = await supabase
        .from("calls")
        .update({
          ai_summary: data.summary,
          ai_sentiment: data.sentiment,
          ai_next_steps: data.next_steps,
          ai_key_topics: data.key_topics,
        })
        .eq("id", callId);

      if (updateError) throw updateError;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calls"] });
      toast.success("Call analyzed successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to analyze call: ${error.message}`);
    },
  });
}
